import type { VariantProps } from "class-variance-authority";
import * as React from "react";
import { forwardRef, useId } from "react";
import { cn } from "@repo/utils";
import { inputVariants } from "./input-variants.js";

export type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> & {
  containerClassName?: string;
} & VariantProps<typeof inputVariants> & {
    label?: string;
    error?: string;
    helperText?: string;
  };

const Input = forwardRef<HTMLInputElement, Props>(
  (
    {
      className,
      containerClassName,
      variant,
      inputSize,
      label,
      error,
      helperText,
      type,
      id,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const hasError = !!error;

    return (
      <div className={cn("w-full", containerClassName)}>
        {label && (
          <label htmlFor={inputId} className="mb-2 block text-sm font-medium">
            {label}
            {props.required && <span className="ml-1 text-red-500">*</span>}
          </label>
        )}
        <input
          id={inputId}
          type={type}
          className={cn(
            inputVariants({ variant: hasError ? "error" : variant, inputSize }),
            className
          )}
          ref={ref}
          aria-invalid={hasError}
          aria-describedby={
            error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
          }
          {...props}
        />
        {error && (
          <p id={`${inputId}-error`} className="mt-1.5 text-sm text-red-500">
            {error}
          </p>
        )}
        {!error && helperText && (
          <p id={`${inputId}-helper`} className="mt-1.5 text-sm text-gray-500">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = "Input"; // For React DevTools

export { Input };
